class AsaPersonalization extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-asa-toggle]');
    this.panel = this.querySelector('[data-asa-panel]');

    if (this.toggle && this.panel) {
      this.toggle.addEventListener('click', () => this.togglePanel());
    }

    this.querySelectorAll('[data-asa-preview-source]').forEach((field) => {
      field.addEventListener('input', () => this.updatePreview(field));
    });

    this.querySelectorAll('.asa-dropdown').forEach((dropdown) => this.initDropdown(dropdown));

    this.querySelectorAll('[data-asa-upload]').forEach((upload) => this.initUpload(upload));

    this.querySelectorAll('[data-preview-text-color]').forEach((input) => {
      input.addEventListener('change', () => this.applyPreviewTextColor(input.dataset.previewTextColor));
    });

    // Choice fields can now arrive pre-selected from Liquid (see the `checked`
    // defaults in asa-personalization.liquid). Nothing dispatches a change
    // event for those, so mirror them into the preview once on load.
    const preselectedFont = this.querySelector('[data-option-font-family]:checked');
    if (preselectedFont) this.applyPreviewFont(preselectedFont.dataset.optionFontFamily);

    const preselectedTextColor = this.querySelector('[data-preview-text-color]:checked');
    if (preselectedTextColor) this.applyPreviewTextColor(preselectedTextColor.dataset.previewTextColor);

    this.initMockup();

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.fitAllPreviewLines(), 150);
    });

    // Custom @font-face fonts load asynchronously, so the very first fit
    // measurement can happen against the fallback font. Re-fit once every
    // font on the page has actually finished loading (covers the case where
    // a font radio is already checked on load, e.g. after a back/forward
    // navigation restores form state).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        const checkedFont = this.querySelector('[data-option-font-family]:checked');
        if (checkedFont) {
          this.updatePreviewFontScale(checkedFont.dataset.optionFontFamily);
        } else {
          this.fitAllPreviewLines();
        }
      });
    }

    // Auto-expand the panel if a required field inside it fails validation,
    // so the browser can actually focus a field the customer can see.
    this.addEventListener(
      'invalid',
      (event) => {
        if (this.panel && this.panel.contains(event.target)) {
          this.expandPanel();
        }
      },
      true
    );

    // Dawn's product form has `novalidate`, so the browser never blocks
    // submission on its own, and Dawn's own AJAX handler (registered directly
    // on the form) would otherwise run first. A capture-phase listener on
    // `document` always runs before any listener on the form itself, so it's
    // the only reliable place to stop the submit before Dawn's handler fires.
    const requiredField = this.querySelector('[required]');
    this.form = requiredField ? requiredField.form : null;

    if (this.form) {
      document.addEventListener(
        'submit',
        (event) => {
          if (event.target !== this.form) return;
          const invalidFields = this.findInvalidFields();
          if (invalidFields.length) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.expandPanel();
            this.showValidationErrors(invalidFields);
          }
        },
        true
      );
    }

    // Drop a field's error the moment it becomes valid, so the form stops
    // shouting at the customer while they are still working through it.
    this.querySelectorAll('[required]').forEach((field) => {
      const clearIfFixed = () => {
        if (!field.checkValidity()) return;
        this.clearFieldError(field);
        const remaining = this.findInvalidFields().length;
        if (remaining === 0) {
          this.clearFormSummaryError();
        } else if (this.summaryError && this.summaryError.isConnected) {
          this.showFormSummaryError(remaining);
        }
      };
      field.addEventListener('change', clearIfFixed);
      field.addEventListener('input', clearIfFixed);
    });
  }

  /* --- Validation feedback -------------------------------------------------
     Every choice field here hides its real <input> at 1x1 with opacity:0 and
     paints a label on top (font dropdown, colour swatches, week-start
     buttons, image upload). The browser cannot anchor its native validation
     bubble to a box that small and invisible, so reportValidity() on one of
     them silently does nothing - which made "Add to cart" behave like a dead
     button whenever the first unfilled field happened to be one of those.
     Render our own message inside the field instead.
  --------------------------------------------------------------------------*/

  findInvalidFields() {
    // One entry per control group: every radio in a group shares a container,
    // and the customer only needs to be told about it once.
    const seenGroups = new Set();
    return Array.from(this.querySelectorAll('[required]')).filter((field) => {
      if (field.checkValidity()) return false;
      const groupKey = field.name || field.id;
      if (seenGroups.has(groupKey)) return false;
      seenGroups.add(groupKey);
      return true;
    });
  }

  findFirstInvalidField() {
    return this.findInvalidFields()[0] || null;
  }

  fieldContainer(field) {
    return field.closest('.asa-personalization__field');
  }

  isFieldVisible(field) {
    if (!field || field.offsetParent === null) return false;
    const rect = field.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return false;
    return parseFloat(getComputedStyle(field).opacity) > 0.01;
  }

  errorMessageFor(field) {
    if (field.type === 'radio' || field.type === 'checkbox') return 'Required - please choose an option.';
    if (field.type === 'file') return 'Required - please upload an image.';
    return 'Required - please fill this in.';
  }

  groupInputs(field) {
    if (!field.name) return [field];
    return Array.from(this.querySelectorAll(`[name="${CSS.escape(field.name)}"]`));
  }

  showFieldError(field) {
    const container = this.fieldContainer(field);
    if (!container) return;

    container.classList.add('asa-personalization__field--error');

    let message = container.querySelector('.asa-field-error');
    if (!message) {
      message = document.createElement('p');
      message.className = 'asa-field-error';
      message.setAttribute('role', 'alert');
      container.appendChild(message);
    }
    message.textContent = this.errorMessageFor(field);

    this.groupInputs(field).forEach((input) => input.setAttribute('aria-invalid', 'true'));
  }

  clearFieldError(field) {
    const container = this.fieldContainer(field);
    if (!container) return;

    container.classList.remove('asa-personalization__field--error');
    const message = container.querySelector('.asa-field-error');
    if (message) message.remove();

    this.groupInputs(field).forEach((input) => input.removeAttribute('aria-invalid'));
  }

  showValidationErrors(fields) {
    fields.forEach((field) => this.showFieldError(field));

    // On a phone the button sits ~1500px below the fields, so a message that
    // only appears up there is a message the customer never sees. Put one
    // right where they just tapped as well.
    this.showFormSummaryError(fields.length);

    const first = fields[0];
    this.scrollToField(this.fieldContainer(first) || first);

    // Put the cursor in the field so the customer can just start typing. No
    // reportValidity() here on purpose: the native bubble only ever appears
    // for the visible text inputs, and where it does appear it lands on top of
    // the message we just rendered. One message, every field, is clearer.
    if (this.isFieldVisible(first)) {
      first.focus({ preventScroll: true });
    }
  }

  // scrollIntoView lands short here: the page header is sticky (~90px) and the
  // product column is sticky too, so the browser's own "center" still leaves
  // the field below the fold on a phone. Work out the offset ourselves, once
  // layout has settled.
  //
  // Instant, not smooth: `behavior: 'smooth'` is silently ignored on these
  // product pages (verified - the scroll simply never happens), and an error
  // the customer is waiting on should snap into view anyway.
  scrollToField(target) {
    requestAnimationFrame(() => {
      const header = document.querySelector('.section-header, sticky-header, .header-wrapper');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    });
  }

  showFormSummaryError(count) {
    if (!this.form) return;
    const button = this.form.querySelector('[name="add"]');
    const anchor = button ? button.closest('.product-form__buttons') : null;
    if (!anchor || !anchor.parentElement) return;

    if (!this.summaryError || !this.summaryError.isConnected) {
      this.summaryError = document.createElement('p');
      this.summaryError.className = 'asa-field-error asa-form-error';
      this.summaryError.setAttribute('role', 'alert');
      anchor.parentElement.insertBefore(this.summaryError, anchor);
    }

    this.summaryError.textContent =
      count === 1
        ? 'Please complete the highlighted field above before adding to cart.'
        : `Please complete the ${count} highlighted fields above before adding to cart.`;
  }

  clearFormSummaryError() {
    if (this.summaryError && this.summaryError.isConnected) this.summaryError.remove();
  }

  togglePanel() {
    const expanded = this.toggle.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      this.collapsePanel();
    } else {
      this.expandPanel();
    }
  }

  expandPanel() {
    this.toggle.setAttribute('aria-expanded', 'true');
    this.panel.classList.remove('asa-personalization__panel--collapsed');
  }

  collapsePanel() {
    this.toggle.setAttribute('aria-expanded', 'false');
    this.panel.classList.add('asa-personalization__panel--collapsed');
  }

  updatePreview(field) {
    const key = field.dataset.asaPreviewSource;
    const target = this.querySelector(`[data-asa-preview-line="${key}"]`);
    if (target) {
      target.textContent = field.value;
      this.fitPreviewLine(target);
    }
  }

  // Sizes a preview line in two passes: first it boosts the font-size to
  // compensate for that particular font's visual weight (see
  // getFontVisualScale), then it shrinks the result just enough to keep the
  // text on one line and inside its box if it would otherwise overflow.
  fitPreviewLine(line) {
    if (!line) return;
    const container = line.parentElement;
    if (!container) return;

    line.style.fontSize = '';
    if (!line.textContent.trim()) return;

    const baseSize = parseFloat(getComputedStyle(line).fontSize);
    if (!baseSize) return;

    const scale = this.previewFontScale || 1;
    let targetSize = baseSize * scale;
    line.style.fontSize = `${targetSize}px`;

    const availableWidth = container.clientWidth;
    const textWidth = line.scrollWidth;
    if (!availableWidth || textWidth <= availableWidth) return;

    // Low floor on purpose: preventing overflow matters more than a hard
    // minimum size, since a long string in a wide/boosted font otherwise
    // keeps spilling out of the box no matter how much we scale it down.
    const minSize = baseSize * 0.3;
    const fitSize = Math.max(minSize, (availableWidth / textWidth) * targetSize * 0.94);
    line.style.fontSize = `${fitSize}px`;
  }

  fitAllPreviewLines() {
    this.querySelectorAll('[data-asa-preview-line]').forEach((line) => this.fitPreviewLine(line));
  }

  // Several of the 28 custom fonts are thin/small-looking script faces that
  // stay hard to read even once they fit the box, while a handful of bold
  // sans faces are already full-sized. Measuring this automatically (e.g. via
  // canvas text metrics) turned out unreliable across browsers/font-loading
  // timing, so instead each font gets a fixed boost tuned by eye from real
  // preview screenshots of all 28 fonts. Unlisted fonts default to 1 (no change).
  static FONT_PREVIEW_SCALE = {
    Halimun: 1.2,
    Dioksinary: 1.7,
    Guigliamore: 1.8,
    Simplemind: 1.5,
    Affinities: 1.55,
    Goldy: 1.5,
    'Lucida Handwriting': 1,
    Gardarena: 1.35,
    Danisha: 1.3,
    Misses: 1.4,
    Bright: 1.3,
    WildYouth: 1.3,
    Shayla: 1.6,
    'The Hungry': 1.3,
    Belting: 1.5,
    Amelia: 1.4,
    Papyrus: 1.05,
    Coasta: 1.1,
    Halva: 1,
    'Play Ground Sans': 1,
    Sophia: 1.3,
    Celestar: 1,
    'Dairy Alettima': 1.4,
    Popstick: 1.05,
    'New Athletic M54': 1,
    'Confilla Bold': 1,
    Charllote: 1.3,
    FuturaBT: 1,
  };

  getFontVisualScale(fontFamily) {
    return AsaPersonalization.FONT_PREVIEW_SCALE[fontFamily] || 1;
  }

  updatePreviewFontScale(fontFamily) {
    this.previewFontScale = this.getFontVisualScale(fontFamily);
    this.fitAllPreviewLines();
  }

  initDropdown(dropdown) {
    const selectedLabel = dropdown.querySelector('[data-asa-dropdown-selected]');
    const selectedImage = dropdown.querySelector('[data-asa-dropdown-selected-image]');

    dropdown.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => {
        if (selectedLabel) selectedLabel.textContent = input.dataset.optionLabel || input.value;
        if (selectedImage) {
          if (input.dataset.optionImage) {
            selectedImage.src = input.dataset.optionImage;
            selectedImage.hidden = false;
          } else {
            selectedImage.hidden = true;
          }
        }
        if (input.dataset.optionFontFamily) this.applyPreviewFont(input.dataset.optionFontFamily);
        dropdown.removeAttribute('open');
      });
    });

    document.addEventListener('click', (event) => {
      if (!dropdown.contains(event.target)) dropdown.removeAttribute('open');
    });
  }

  applyPreviewFont(fontFamily) {
    const value = `'${fontFamily}', cursive, serif`;
    this.querySelectorAll('[data-asa-preview-line]').forEach((line) => {
      line.style.fontFamily = value;
    });

    // The boost factor is a fixed lookup, so it applies immediately.
    this.updatePreviewFontScale(fontFamily);

    // The overflow-shrink pass in fitPreviewLine measures rendered text
    // width, which is only accurate once the @font-face has actually
    // finished loading - re-run it once that happens (falls back to the
    // fallback font's width briefly, which just re-corrects itself here).
    if (document.fonts && document.fonts.load) {
      document.fonts
        .load(`16px "${fontFamily}"`)
        .catch(() => {})
        .then(() => this.fitAllPreviewLines());
    }
  }

  applyPreviewTextColor(color) {
    const preview = this.querySelector('.asa-preview');
    const isMockup = preview && preview.classList.contains('asa-preview--mockup');
    const lightText = this.isLightColor(color);

    this.querySelectorAll('[data-asa-preview-line]').forEach((line) => {
      line.style.color = color;
      // A photo background can't swap to stay behind the text, so give the
      // text a contrasting outline instead of recoloring the container.
      line.style.textShadow = isMockup
        ? lightText
          ? '0 0 2px rgba(0, 0, 0, 0.65), 0 0 8px rgba(0, 0, 0, 0.45)'
          : '0 0 8px rgba(255, 255, 255, 0.55)'
        : '';
    });

    if (preview && !isMockup) {
      preview.style.backgroundColor = lightText ? '#2b2b2b' : '#f4f2ee';
    }
  }

  initMockup() {
    const mockup = this.querySelector('.asa-preview--mockup');
    const image = this.querySelector('[data-asa-mockup-image]');
    if (!mockup || !image) return;

    this.mockupImages = {};
    Object.keys(mockup.dataset).forEach((key) => {
      // dataset.mockupSilverLight (from data-mockup-silver-light) -> "silver-light"
      const variantKey = key
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase()
        .replace(/^mockup-/, '');
      this.mockupImages[variantKey] = mockup.dataset[key];
    });
    this.mockupImage = image;

    this.querySelectorAll('[data-swatch-field="hardware_color"]').forEach((input) => {
      input.addEventListener('change', () => this.updateMockupImage());
    });
    this.querySelectorAll('[data-swatch-field="text_color"]').forEach((input) => {
      input.addEventListener('change', () => this.updateMockupImage());
    });

    this.uploadImageIsLight = null;
    this.updateMockupImage();
  }

  updateMockupImage() {
    if (!this.mockupImage) return;

    const hardwareInput = this.querySelector('[data-swatch-field="hardware_color"]:checked');
    const textInput = this.querySelector('[data-swatch-field="text_color"]:checked');

    const hardwareKey = (hardwareInput ? hardwareInput.value : 'silver').toLowerCase();
    let bgKey = 'light';
    if (textInput && textInput.dataset.previewTextColor) {
      bgKey = this.isLightColor(textInput.dataset.previewTextColor) ? 'dark' : 'light';
    } else if (this.uploadImageIsLight !== null) {
      // No text-color swatch to go by (image-upload products), so pick the
      // background that keeps a light or dark uploaded logo readable instead.
      bgKey = this.uploadImageIsLight ? 'dark' : 'light';
    }

    const candidates = [
      `${hardwareKey}-${bgKey}`,
      `${hardwareKey}-light`,
      `${hardwareKey}-dark`,
      `silver-${bgKey}`,
      'silver-light',
    ];
    const url = candidates.map((key) => this.mockupImages[key]).find(Boolean) || Object.values(this.mockupImages)[0];

    if (url) this.mockupImage.src = url;
  }

  isLightColor(hex) {
    const value = hex.replace('#', '');
    if (value.length !== 6) return false;
    const r = parseInt(value.substring(0, 2), 16);
    const g = parseInt(value.substring(2, 4), 16);
    const b = parseInt(value.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6;
  }

  initUpload(upload) {
    const input = upload.querySelector('input[type="file"]');
    const dropzone = upload.querySelector('[data-asa-upload-dropzone]');
    const previewWrap = upload.querySelector('[data-asa-upload-preview]');
    const previewImage = upload.querySelector('[data-asa-upload-preview-image]');
    const removeButton = upload.querySelector('[data-asa-upload-remove]');
    const mockupOverlay = this.querySelector('[data-asa-preview-upload-image]');

    if (!input) return;

    dropzone.addEventListener('click', () => input.click());

    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (!file) return;
      if (previewImage.dataset.objectUrl) URL.revokeObjectURL(previewImage.dataset.objectUrl);
      const url = URL.createObjectURL(file);
      previewImage.src = url;
      previewImage.dataset.objectUrl = url;
      previewWrap.hidden = false;
      dropzone.hidden = true;

      if (mockupOverlay) {
        mockupOverlay.src = url;
        mockupOverlay.hidden = false;
        this.computeImageBrightness(url).then((isLight) => {
          this.uploadImageIsLight = isLight;
          this.updateMockupImage();
        });
      }
    });

    if (removeButton) {
      removeButton.addEventListener('click', (event) => {
        event.preventDefault();
        input.value = '';
        previewWrap.hidden = true;
        dropzone.hidden = false;
        if (previewImage.dataset.objectUrl) {
          URL.revokeObjectURL(previewImage.dataset.objectUrl);
          delete previewImage.dataset.objectUrl;
        }
        previewImage.removeAttribute('src');

        if (mockupOverlay) {
          mockupOverlay.hidden = true;
          mockupOverlay.removeAttribute('src');
          this.uploadImageIsLight = null;
          this.updateMockupImage();
        }
      });
    }
  }

  computeImageBrightness(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(false);
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);
        let data;
        try {
          data = ctx.getImageData(0, 0, size, size).data;
        } catch (error) {
          // Canvas can be tainted for cross-origin images; fall back to a safe default.
          resolve(false);
          return;
        }
        let total = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha === 0) continue;
          total += (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
          count += 1;
        }
        resolve(count ? total / count > 0.6 : false);
      };
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }
}

customElements.define('asa-personalization', AsaPersonalization);
